import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { 
  onAuthStateChanged, 
  signOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            // Update lastLogin
            await setDoc(doc(db, 'users', firebaseUser.uid), {
              lastLogin: serverTimestamp()
            }, { merge: true });
            
            setUser({
              id: firebaseUser.uid,
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              prenume: userData.prenume,
              isGuest: userData.isGuest || false,
              createdAt: userData.createdAt,
              lastLogin: userData.lastLogin
            });
          } else {
            // Create user document if it doesn't exist
            const newUser = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              prenume: firebaseUser.displayName || firebaseUser.email.split('@')[0],
              isGuest: false,
              createdAt: serverTimestamp(),
              lastLogin: serverTimestamp()
            };
            
            await setDoc(doc(db, 'users', firebaseUser.uid), newUser);
            
            setUser({
              id: firebaseUser.uid,
              ...newUser
            });
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Auth state change error:', error);
        setUser(null);
      } finally {
        setAuthInitialized(true);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential;
    } catch (error) {
      console.error('Login error:', error);
      
      if (error.code === 'auth/user-not-found') {
        throw new Error('Nu există un cont cu acest email. Verifică adresa sau creează un cont nou.');
      } else if (error.code === 'auth/wrong-password') {
        throw new Error('Parola este incorectă. Încearcă din nou sau resetează parola.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Formatul email-ului este invalid.');
      } else if (error.code === 'auth/user-disabled') {
        throw new Error('Acest cont a fost dezactivat. Contactează administratorul.');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Prea multe încercări nereusite. Încearcă din nou în câteva minute.');
      } else if (error.code === 'auth/network-request-failed') {
        throw new Error('Probleme de conexiune. Verifică internetul și încearcă din nou.');
      }
      
      throw error;
    }
  }, []);

  const register = useCallback(async (email, password, prenume) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Create user document with proper structure
      const userData = {
        uid: user.uid,
        email: email,
        prenume: prenume,
        isGuest: false,
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp()
      };
      
      await setDoc(doc(db, 'users', user.uid), userData);
      
      return {
        ...userCredential,
        success: true,
        message: `Cont creat cu succes! 

✅ Te poți conecta imediat cu datele introduse.

Bun venit la Calculator UMFCD!`
      };
    } catch (error) {
      console.error('Registration error:', error);
      
      if (error.code === 'auth/email-already-in-use') {
        throw new Error('Există deja un cont cu acest email. Încearcă să te conectezi.');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('Parola este prea slabă. Folosește cel puțin 6 caractere.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Formatul email-ului este invalid.');
      }
      
      throw error;
    }
  }, []);

  const resetPassword = useCallback(async (email) => {
    try {
      await sendPasswordResetEmail(auth, email, {
        url: window.location.origin,
        handleCodeInApp: false
      });
      
      return {
        success: true,
        message: `Email de resetare trimis cu succes! 

📧 Verifică inbox-ul la ${email}

Pentru a reseta parola:
1️⃣ Verifică inbox-ul și spam-ul
2️⃣ Click pe link-ul din email
3️⃣ Urmează instrucțiunile pentru parola nouă

💡 Link-ul de resetare este valid 1 oră.`
      };
    } catch (error) {
      console.error('Password reset error:', error);
      
      if (error.code === 'auth/user-not-found') {
        throw new Error('Nu există un cont cu acest email. Verifică adresa sau creează un cont nou.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Formatul email-ului este invalid.');
      }
      
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setAuthInitialized(true);
      setLoading(false);
    }
  }, []);

  const value = useMemo(() => ({
    user: user || null,
    authInitialized,
    loading,
    login,
    register,
    resetPassword,
    logout
  }), [user, authInitialized, loading, login, register, resetPassword, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
